import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { SignatureConsent } from './signature-consent.component';
import { FileService } from '@shared/services/core/file.service';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import * as uuid from 'uuid';
import { NotificationService } from '@shared/services/core/notification.service';

export interface UploadResponse {
	Bucket: string;
	ETag: string;
	Key: string;
	Location: string;
	ServerSideEncryption: string;
	Extras: string;
	key: string;
	originating_identifier: string;
}

@Injectable({ providedIn: 'root' })
export class SignatureConsentService {
	constructor(
		private readonly fileService: FileService,
		private readonly modalController: ModalController,
        private notificationService: NotificationService,
	) {}

	public async openModal(uploadPath: string, isInitials: boolean): Promise<UploadResponse> {
		if (!SignatureConsent.isModalOpen) {
			const modal = await this.modalController.create({
				component: SignatureConsent,
				componentProps: { uploadPath: uploadPath, isInitials: isInitials },
				cssClass: 'fullscreen',
			});

			SignatureConsent.isModalOpen = true;
			await modal.present();
			const { data } = await modal.onWillDismiss();
			SignatureConsent.isModalOpen = false;

			return data;
		}
	}

	public trimAndUpload(signatureDataUrl: string, path: string): Observable<any> {
		return this.trimImage(signatureDataUrl).pipe(
            switchMap((trimmedDataUrl) => {
                return this.notificationService.presentLoading({ message: 'Uploading signature...' }).pipe(
                    map((loader) => {
                        return { trimmedDataUrl, loader };
                    })
                );
            }),
			switchMap(({ trimmedDataUrl, loader }) => {
				return this.fileService.uploadFile2({
					rawFile: { dataUrl: trimmedDataUrl, format: 'png' },
					fileName: uuid.v4(),
					path: path,
					mime: 'image/png',
					isSignature: true,
				}).pipe(tap(() => this.notificationService.dismissLoader(loader)));
			})
		);
	}

	private trimImage(signatureDataUrl: string): Observable<string> {
		return new Observable(observer => {
			const original = new Image();
			original.src = signatureDataUrl;

			original.onload = () => {
				let trimmedCanvas = document.createElement('canvas');
				let trimmedContext = trimmedCanvas.getContext('2d');

				trimmedCanvas.width = original.width;
				trimmedCanvas.height = original.height;
				trimmedContext.drawImage(original, 0, 0);

				let width = trimmedCanvas.width;
				let height = trimmedCanvas.height;
				let pixels = { x: [], y: [] };
				let imageData = trimmedContext.getImageData(0, 0, trimmedCanvas.width, trimmedCanvas.height);
				let x: number, y: number, index: number;

				for (y = 0; y < height; y++) {
					for (x = 0; x < width; x++) {
						index = y * width + x;
						if (imageData.data[index * 4 + 3] > 0) {
							pixels.x.push(x);
							pixels.y.push(y);
						}
					}
				}
				pixels.x.sort((a, b) => a - b);
				pixels.y.sort((a, b) => a - b);
				let n = pixels.x.length - 1;

				width = pixels.x[n] - pixels.x[0];
				height = pixels.y[n] - pixels.y[0];
				let trimmed = trimmedContext.getImageData(pixels.x[0], pixels.y[0], width, height);

				trimmedCanvas.width = width;
				trimmedCanvas.height = height;
				trimmedContext.putImageData(trimmed, 0, 0);

				observer.next(trimmedCanvas.toDataURL());
				observer.complete();
			};

			original.onerror = error => {
				observer.error(error);
			};
		});
	}
}
